import { FirebaseError } from 'firebase/app';
import type { UserProfile } from '@/types/user';

export const FIRESTORE_ERROR_MESSAGES = {
  permission:
    'No tenés permisos para realizar esta acción.',
  rulesBlocked:
    'La operación fue bloqueada por reglas de seguridad.',
  pending:
    'Tu usuario está pendiente de aprobación.',
  inactive:
    'Tu usuario está inactivo.',
  network:
    'No se pudo conectar con la base de datos. Revisá tu conexión e intentá de nuevo.',
  default: 'No se pudo completar la operación. Intentá de nuevo.',
} as const;

const CONTEXT_MESSAGES: Record<string, string> = {
  category:
    'No se pudo guardar la categoría. Solo administradores pueden modificar el catálogo.',
  subcategory: 'No se pudo guardar la subcategoría.',
  customField: 'No se pudo guardar el campo personalizado.',
  item: 'No se pudo guardar el artículo.',
  reservation: 'No se pudo guardar la reserva.',
  client: 'No se pudo guardar el cliente.',
  commercialOperation: 'No se pudo guardar la operación comercial.',
  logisticsOrder: 'No se pudo guardar la orden de logística.',
  logisticsProvider: 'No se pudo guardar el proveedor de logística.',
  invoice: 'No se pudo guardar la factura.',
  maintenance: 'No se pudo guardar la tarea de mantenimiento.',
  user: 'No se pudo guardar el usuario.',
  list: 'No se pudo cargar los datos.',
};

export type FirestoreErrorContext = keyof typeof CONTEXT_MESSAGES;

const CATALOG_CONTEXTS: readonly FirestoreErrorContext[] = [
  'category',
  'subcategory',
  'customField',
];

/** Hint cuando admin/superadmin recibe permission-denied (reglas no publicadas o perfil desalineado). */
export const CATALOG_RULES_DEPLOY_HINT =
  'Si tu rol es administrador, verificá que las reglas estén desplegadas (corepack pnpm firebase:reauth && corepack pnpm firestore:deploy) y que tu perfil en users/{uid} tenga role, active: true y companyId: default. Ver docs/PAGES_DEPLOY.md y FIRESTORE_RULES_TEST_PLAN.md (Fase 0).';

export function shouldShowCatalogDeployHint(
  profile: UserProfile | null | undefined,
): boolean {
  if (!profile?.active) return false;
  return profile.role === 'superadmin' || profile.role === 'admin';
}

function appendCatalogDeployHint(
  message: string,
  context: FirestoreErrorContext,
  profile?: UserProfile | null,
): string {
  if (
    CATALOG_CONTEXTS.includes(context) &&
    shouldShowCatalogDeployHint(profile)
  ) {
    return `${message} ${CATALOG_RULES_DEPLOY_HINT}`;
  }
  return message;
}

function permissionMessageForProfile(
  profile: UserProfile | null | undefined,
): string {
  if (!profile) {
    return `${FIRESTORE_ERROR_MESSAGES.permission} ${FIRESTORE_ERROR_MESSAGES.rulesBlocked}`;
  }
  if (profile.role === 'pending' || !profile.active) {
    return profile.role === 'pending'
      ? FIRESTORE_ERROR_MESSAGES.pending
      : FIRESTORE_ERROR_MESSAGES.inactive;
  }
  return `${FIRESTORE_ERROR_MESSAGES.permission} ${FIRESTORE_ERROR_MESSAGES.rulesBlocked}`;
}

export async function withFirestoreError<T>(
  fn: () => Promise<T>,
  context: FirestoreErrorContext = 'list',
  profile?: UserProfile | null,
): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    throw new Error(formatFirestoreError(err, context, profile));
  }
}

export function formatFirestoreError(
  err: unknown,
  context: FirestoreErrorContext = 'list',
  profile?: UserProfile | null,
): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case 'permission-denied': {
        const base =
          CONTEXT_MESSAGES[context] ??
          permissionMessageForProfile(profile);
        return appendCatalogDeployHint(base, context, profile);
      }
      case 'unavailable':
      case 'deadline-exceeded':
        return FIRESTORE_ERROR_MESSAGES.network;
      case 'not-found':
        return 'El documento solicitado no existe.';
      case 'already-exists':
        return 'El registro ya existe.';
      case 'failed-precondition':
        return 'Falta un índice en Firestore. Contactá al administrador.';
      default:
        return CONTEXT_MESSAGES[context] ?? FIRESTORE_ERROR_MESSAGES.default;
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return CONTEXT_MESSAGES[context] ?? FIRESTORE_ERROR_MESSAGES.default;
}
